// src/app/shared/services/favorites.service.ts
import { Injectable } from '@angular/core';

export interface FavoritesSection {
  alias: string;
  nodeIds?: string[]; // for sections that store node ids
  items?: any[]; // for sections that store items (e.g. openDayEvents)
}

export interface FavoritesStorage {
  baCategories?: FavoritesSection;
  combinations?: FavoritesSection;
  scholarships?: FavoritesSection;
  programsForExcellence?: FavoritesSection;
  scholarshipsAdvanced?: FavoritesSection;
  extendedGeneralCatalog?: FavoritesSection;
  openDayEvents?: FavoritesSection;
  portalCatalog?: FavoritesSection;
  // allow other keys in the future
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class FavoritesService {
  private storageKey = 'favorites';

  constructor() {}

  // the exact default shape you requested
  private defaultStorage(): FavoritesStorage {
    return {
      baCategories: { alias: 'bACategory', nodeIds: [] },
      combinations: { alias: 'combination', nodeIds: [] },
      scholarships: { alias: 'scholarship', nodeIds: [] },
      programsForExcellence: { alias: 'programForExcellence', nodeIds: [] },
      scholarshipsAdvanced: { alias: 'scholarshipAdvance', nodeIds: [] },
      extendedGeneralCatalog: { alias: 'extendedGeneralCatalog', nodeIds: [] },
      openDayEvents: { alias: 'openDayEvents', items: [] },
      portalCatalog: { alias: 'portalCatalog', nodeIds: [] },
    };
  }

  private writeStorage(data: FavoritesStorage): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (e) {
      console.error('Could not write favorites to localStorage', e);
    }
  }

  
  private readStorage(): FavoritesStorage {
    const raw = localStorage.getItem(this.storageKey);
    if (!raw) {
      const def = this.defaultStorage();
      this.writeStorage(def);
      return def;
    }

    try {
      const parsed = JSON.parse(raw) as FavoritesStorage;
      const defaults = this.defaultStorage();

      const merged: FavoritesStorage = { ...defaults, ...parsed };

      if (!merged.portalCatalog)
        merged.portalCatalog = { alias: 'portalCatalog', nodeIds: [] };
      if (!Array.isArray(merged.portalCatalog.nodeIds))
        merged.portalCatalog.nodeIds = [];

      Object.keys(defaults).forEach((k) => {
        const defVal = (defaults as any)[k];
        if (defVal && defVal.nodeIds && !(merged as any)[k]?.nodeIds) {
          (merged as any)[k].nodeIds = [];
        }
        if (defVal && defVal.items && !(merged as any)[k]?.items) {
          (merged as any)[k].items = [];
        }
      });

      return merged;
    } catch (e) {
      const def = this.defaultStorage();
      this.writeStorage(def);
      return def;
    }
  }

  
  private getStoreAndPortal(): {
    store: FavoritesStorage;
    portal: { alias: string; nodeIds: string[] };
  } {
    const store = this.readStorage();

    if (!store.portalCatalog) {
      store.portalCatalog = { alias: 'portalCatalog', nodeIds: [] };
    }
    if (!Array.isArray(store.portalCatalog.nodeIds)) {
      store.portalCatalog.nodeIds = [];
    }

    return {
      store,
      portal: store.portalCatalog as { alias: string; nodeIds: string[] },
    };
  }

  
  isFavorite(
    nodeId: string | number
  ): boolean {
    const { portal } = this.getStoreAndPortal();
    return portal.nodeIds.includes(`${nodeId}`);
  }

 
  toggleFavorite(
    nodeId: string | number
  ): boolean {
    const { store, portal } = this.getStoreAndPortal();

    const nId = `${nodeId}`;
    const idx = portal.nodeIds.indexOf(nId);

    if (idx === -1) {
      portal.nodeIds.push(nId);
      this.writeStorage(store);
      return true;
    } else {
      portal.nodeIds.splice(idx, 1);
      this.writeStorage(store);
      return false;
    }
  }

 
  getFavoritesForCatalog(): string[] {
    const { portal } = this.getStoreAndPortal();
    return [...portal.nodeIds];
  }

  
  clearAll(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (e) {
      console.error('Could not remove favorites from localStorage', e);
    }
  }
}
